/// Auto-generated with ScanbotSDKCodegenV3. Modifications will be overwritten.
/// Generated from core/schemas/DocumentScannerTypes.yaml
import { ToJsonConfiguration } from '../common/JsonSerializationTypes';
import { AspectRatio, LineSegmentFloat, LineSegmentInt } from '../geometry/Geometry';
import { ImageRef } from '../image/image';
/** @hidden */
export const DocumentScannerEngineModeValues = ['ML', 'LEGACY'];
/**
Parameters for the document scanner.
*/
export class DocumentScannerParameters {
    /** @param source {@displayType `DeepPartial<DocumentScannerParameters>`} */
    constructor(source = {}) {
        /**
          The minimum score in percent (0 - 100) of the perspective distortion to accept a detected document.
          Set lower values to accept more perspective distortion.
          
          Warning: Lower values result in more blurred document images.
          
          Default is 75
          */
        this.acceptedAngleScore = 75;
        /**
          The minimum size in percent (0 - 100) of the screen size to accept a detected document.
          It is sufficient that height or width match the score.
          
          Warning: Lower values result in low resolution document images.
          
          Default is 80
          */
        this.acceptedSizeScore = 80;
        /**
          The minimum brightness value (0-255) to accept a detected document.
          
          Default is 0
          */
        this.acceptedBrightnessThreshold = 0;
        /**
          The minimum score in percent (0 - 100) that the aspect ratio of the document
          must match one of the required aspect ratios (if any) to accept a detected document.
          If acceptedAspectRatioScore is more than 0, then the document is only accepted if the aspect ratio
          matches one of the given aspect ratios (if any), otherwise OK_BUT_BAD_ASPECT_RATIO is returned.
          
          Default is 85
          */
        this.acceptedAspectRatioScore = 85;
        /**
          The possible desired aspect ratios for the detected document.
          A document matches if its aspect ratio matches any of the given aspect ratios.
          If acceptedAspectRatioScore is more than 0, then the document is only accepted if the aspect ratio
          matches one of the given aspect ratios, otherwise OK_BUT_BAD_ASPECT_RATIO is returned.
          If empty, no aspect ratio is preferred.
          */
        this.aspectRatios = [];
        /**
          If false, the document scanner will return OK_BUT_ORIENTATION_MISMATCH if the
          detected document orientation does not match the input image orientation,
          e.g. if the document is detected as landscape but the input image is portrait.
          If true, the document scanner will ignore orientation mismatches.
          
          Default is false
          */
        this.ignoreOrientationMismatch = false;
        /**
          If the document scanner confidence, in percent (0 - 100), that the input is a cropped document is above this
          threshold it will return a status of OK_BUT_ALREADY_CROPPED.
          
          Only used in ML engine mode and only when scanning single images.
          
          Default is 80
          */
        this.alreadyCroppedScoreThreshold = 80;
        if (source.acceptedAngleScore !== undefined) {
            this.acceptedAngleScore = source.acceptedAngleScore;
        }
        if (source.acceptedSizeScore !== undefined) {
            this.acceptedSizeScore = source.acceptedSizeScore;
        }
        if (source.acceptedBrightnessThreshold !== undefined) {
            this.acceptedBrightnessThreshold = source.acceptedBrightnessThreshold;
        }
        if (source.acceptedAspectRatioScore !== undefined) {
            this.acceptedAspectRatioScore = source.acceptedAspectRatioScore;
        }
        if (source.aspectRatios !== undefined) {
            this.aspectRatios = source.aspectRatios.map((it) => {
                return new AspectRatio(it);
            });
        }
        if (source.ignoreOrientationMismatch !== undefined) {
            this.ignoreOrientationMismatch = source.ignoreOrientationMismatch;
        }
        if (source.alreadyCroppedScoreThreshold !== undefined) {
            this.alreadyCroppedScoreThreshold = source.alreadyCroppedScoreThreshold;
        }
    }
}
/**
Configuration for handling partially visible documents.
*/
export class PartiallyVisibleDocumentConfiguration {
    /** @param source {@displayType `DeepPartial<PartiallyVisibleDocumentConfiguration>`} */
    constructor(source = {}) {
        /**
          If true, the document scanner will return a status of ERROR_PARTIALLY_VISIBLE or ERROR_PARTIALLY_VISIBLE_TOO_CLOSE
          if a document-like shape is detected, but not all four corners of the document are visible.
          If false, a status of ERROR_NOTHING_DETECTED will be returned instead.
          
          Only used in ML engine mode.
          
          Default is false
          */
        this.allowPartiallyVisibleDocuments = false;
        /**
          Live mode only: Duration in milliseconds over which the document has to be detected as partially visible. If
          on any frame the document is not detected as partially visible, or detected as having a completely different
          Displacement, the accumulation will be reset. During the accumulation, the document scanner will return a
          status of ERROR_NOTHING_DETECTED instead of one of the partially visible statuses. Set to 0 to disable this
          behavior.
          
          In single-shot mode, the document scanner will return ERROR_PARTIALLY_VISIBLE if a document is detected as
          partially visible, regardless of the value of this parameter.
          
          Default is 700
          */
        this.accumulationDuration = 700;
        /**
          Live mode only: Time in milliseconds during which the detection status ERROR_NOTHING_DETECTED is changed to
          ERROR_PARTIALLY_VISIBLE_TOO_CLOSE if no document was detected following the detection of a partially visible
          document. Set to 0 to disable this behavior.
          
          In single-shot mode this parameter has no effect.
          
          Default is 1500
          */
        this.retentionTime = 1500;
        /**
          Minimum brightness of a partially visible potential document. Increasing this value can reduce the number
          of spurious detections.
          
          Default is 0
          */
        this.minimumBrightness = 0;
        if (source.allowPartiallyVisibleDocuments !== undefined) {
            this.allowPartiallyVisibleDocuments = source.allowPartiallyVisibleDocuments;
        }
        if (source.accumulationDuration !== undefined) {
            this.accumulationDuration = source.accumulationDuration;
        }
        if (source.retentionTime !== undefined) {
            this.retentionTime = source.retentionTime;
        }
        if (source.minimumBrightness !== undefined) {
            this.minimumBrightness = source.minimumBrightness;
        }
    }
}
/**
Configuration for the document scanner.
*/
export class DocumentScannerConfiguration {
    /** @param source {@displayType `DeepPartial<DocumentScannerConfiguration>`} */
    constructor(source = {}) {
        /**
          The engine to use for document scanning.
          
          Default is ML
          */
        this.engineMode = 'ML';
        /**
          In live mode, the document scanner will return only the best document contour. In single-shot mode it will return additional document contours, suitable for use in the cropping screen.
          
          Default is AUTO
          */
        this.processingMode = 'AUTO';
        /**
          Initial parameters for the document scanner.
          */
        this.parameters = new DocumentScannerParameters({});
        /**
          Configuration for handling partially visible documents.
          
          Has an effect only:
          - with ML engine mode
          - when configured with allowPartiallyVisibleDocuments set to true.
          */
        this.partiallyVisibleDocumentConfiguration = new PartiallyVisibleDocumentConfiguration({});
        if (source.engineMode !== undefined) {
            this.engineMode = source.engineMode;
        }
        if (source.processingMode !== undefined) {
            this.processingMode = source.processingMode;
        }
        if (source.parameters !== undefined) {
            this.parameters = new DocumentScannerParameters(source.parameters);
        }
        if (source.partiallyVisibleDocumentConfiguration !== undefined) {
            this.partiallyVisibleDocumentConfiguration = new PartiallyVisibleDocumentConfiguration(source.partiallyVisibleDocumentConfiguration);
        }
    }
}
/** @hidden */
export const DocumentDisplacementValues = [
    'NONE',
    'UP',
    'UPPER_RIGHT',
    'RIGHT',
    'LOWER_RIGHT',
    'DOWN',
    'LOWER_LEFT',
    'LEFT',
    'UPPER_LEFT',
];
/** @hidden */
export const DocumentDetectionStatusValues = [
    'NOT_ACQUIRED',
    'OK',
    'OK_BUT_TOO_SMALL',
    'OK_BUT_BAD_ANGLES',
    'OK_BUT_BAD_ASPECT_RATIO',
    'OK_BUT_ORIENTATION_MISMATCH',
    'OK_BUT_OFF_CENTER',
    'OK_BUT_TOO_DARK',
    'OK_BUT_ALREADY_CROPPED',
    'ERROR_NOTHING_DETECTED',
    'ERROR_PARTIALLY_VISIBLE',
    'ERROR_PARTIALLY_VISIBLE_TOO_CLOSE',
    'ERROR_TOO_DARK',
    'ERROR_TOO_NOISY',
];
/**
The total and partial scores for the detected document contour.
*/
export class DocumentDetectionScores {
    /** @param source {@displayType `DeepPartial<DocumentDetectionScores>`} */
    constructor(source = {}) {
        if (source.totalScore !== undefined) {
            this.totalScore = source.totalScore;
        }
        else {
            throw new Error('totalScore must be present in constructor argument');
        }
        if (source.distanceScore !== undefined) {
            this.distanceScore = source.distanceScore;
        }
        else {
            throw new Error('distanceScore must be present in constructor argument');
        }
        if (source.angleScore !== undefined) {
            this.angleScore = source.angleScore;
        }
        else {
            throw new Error('angleScore must be present in constructor argument');
        }
        if (source.sizeScore !== undefined) {
            this.sizeScore = source.sizeScore;
        }
        else {
            throw new Error('sizeScore must be present in constructor argument');
        }
        if (source.aspectRatioScore !== undefined) {
            this.aspectRatioScore = source.aspectRatioScore;
        }
        else {
            throw new Error('aspectRatioScore must be present in constructor argument');
        }
        if (source.lineCoverageScore !== undefined) {
            this.lineCoverageScore = source.lineCoverageScore;
        }
        else {
            throw new Error('lineCoverageScore must be present in constructor argument');
        }
        if (source.widthScore !== undefined) {
            this.widthScore = source.widthScore;
        }
        else {
            throw new Error('widthScore must be present in constructor argument');
        }
        if (source.heightScore !== undefined) {
            this.heightScore = source.heightScore;
        }
        else {
            throw new Error('heightScore must be present in constructor argument');
        }
        if (source.alreadyCroppedScore !== undefined) {
            this.alreadyCroppedScore = source.alreadyCroppedScore;
        }
        else {
            throw new Error('alreadyCroppedScore must be present in constructor argument');
        }
    }
    serialize(config = new ToJsonConfiguration()) {
        return {
            totalScore: this.totalScore,
            distanceScore: this.distanceScore,
            angleScore: this.angleScore,
            sizeScore: this.sizeScore,
            aspectRatioScore: this.aspectRatioScore,
            lineCoverageScore: this.lineCoverageScore,
            widthScore: this.widthScore,
            heightScore: this.heightScore,
            alreadyCroppedScore: this.alreadyCroppedScore,
        };
    }
}
/**
Result of the document contour detection.
*/
export class DocumentDetectionResult {
    /** @param source {@displayType `DeepPartial<DocumentDetectionResult>`} */
    constructor(source = {}) {
        /**
          Detection status.
          
          Default is NOT_ACQUIRED
          */
        this.status = 'NOT_ACQUIRED';
        /**
          Average brightness, calculated as the average
          of the Value channel in the HSV color space of:
          - the whole image, if no document was detected
          - the document crop, if a document was detected
          Ranges from 0 to 255.
          
          Default is 0
          */
        this.averageBrightness = 0;
        /**
          ML engine mode only: The direction towards the center of the document if the document is partially visible.
          
          Requires allowPartiallyVisibleDocuments to be true.
          
          Default is NONE
          */
        this.displacement = 'NONE';
        if (source.status !== undefined) {
            this.status = source.status;
        }
        if (source.detectionScores !== undefined) {
            this.detectionScores = new DocumentDetectionScores(source.detectionScores);
        }
        else {
            throw new Error('detectionScores must be present in constructor argument');
        }
        if (source.points !== undefined) {
            this.points = source.points.map((it) => {
                return { x: it.x, y: it.y };
            });
        }
        else {
            throw new Error('points must be present in constructor argument');
        }
        if (source.horizontalLines !== undefined) {
            this.horizontalLines = source.horizontalLines.map((it) => {
                return new LineSegmentInt(it);
            });
        }
        else {
            throw new Error('horizontalLines must be present in constructor argument');
        }
        if (source.verticalLines !== undefined) {
            this.verticalLines = source.verticalLines.map((it) => {
                return new LineSegmentInt(it);
            });
        }
        else {
            throw new Error('verticalLines must be present in constructor argument');
        }
        if (source.pointsNormalized !== undefined) {
            this.pointsNormalized = source.pointsNormalized.map((it) => {
                return { x: it.x, y: it.y };
            });
        }
        else {
            throw new Error('pointsNormalized must be present in constructor argument');
        }
        if (source.horizontalLinesNormalized !== undefined) {
            this.horizontalLinesNormalized = source.horizontalLinesNormalized.map((it) => {
                return new LineSegmentFloat(it);
            });
        }
        else {
            throw new Error('horizontalLinesNormalized must be present in constructor argument');
        }
        if (source.verticalLinesNormalized !== undefined) {
            this.verticalLinesNormalized = source.verticalLinesNormalized.map((it) => {
                return new LineSegmentFloat(it);
            });
        }
        else {
            throw new Error('verticalLinesNormalized must be present in constructor argument');
        }
        if (source.aspectRatio !== undefined) {
            this.aspectRatio = source.aspectRatio;
        }
        else {
            throw new Error('aspectRatio must be present in constructor argument');
        }
        if (source.averageBrightness !== undefined) {
            this.averageBrightness = source.averageBrightness;
        }
        if (source.displacement !== undefined) {
            this.displacement = source.displacement;
        }
        if (source.displacementVector !== undefined) {
            this.displacementVector = { x: source.displacementVector.x, y: source.displacementVector.y };
        }
        else {
            throw new Error('displacementVector must be present in constructor argument');
        }
    }
    serialize(config = new ToJsonConfiguration()) {
        return {
            status: this.status,
            detectionScores: this.detectionScores.serialize(config),
            points: this.points.map((it) => {
                return it;
            }),
            horizontalLines: this.horizontalLines.map((it) => {
                return it.serialize(config);
            }),
            verticalLines: this.verticalLines.map((it) => {
                return it.serialize(config);
            }),
            pointsNormalized: this.pointsNormalized.map((it) => {
                return it;
            }),
            horizontalLinesNormalized: this.horizontalLinesNormalized.map((it) => {
                return it.serialize(config);
            }),
            verticalLinesNormalized: this.verticalLinesNormalized.map((it) => {
                return it.serialize(config);
            }),
            aspectRatio: this.aspectRatio,
            averageBrightness: this.averageBrightness,
            displacement: this.displacement,
            displacementVector: this.displacementVector,
        };
    }
}
/**
Result of the document detection followed by cropping of the found document.
*/
export class DocumentScanningResult {
    /** @param source {@displayType `DeepPartial<DocumentScanningResult>`} */
    constructor(source = {}) {
        /**
          Cropped document image.
          */
        this.croppedImage = null;
        this._released = false;
        if (source.detectionResult !== undefined) {
            this.detectionResult = new DocumentDetectionResult(source.detectionResult);
        }
        else {
            throw new Error('detectionResult must be present in constructor argument');
        }
        if (source.croppedImage !== undefined) {
            this.croppedImage = source.croppedImage != null ? ImageRef.from(source.croppedImage) : null;
        }
    }
    async serialize(config = new ToJsonConfiguration()) {
        return {
            detectionResult: this.detectionResult.serialize(config),
            croppedImage: config.serializeImages
                ? this.croppedImage != null
                    ? await this.croppedImage.serialize(config.imageSerializationMode)
                    : null
                : undefined,
        };
    }
    release() {
        if (this._released) {
            return;
        }
        {
            if (this.croppedImage != null) {
                this.croppedImage.release();
            }
        }
        this._released = true;
    }
    async encodeImages() {
        if (this.croppedImage != null) {
            await this.croppedImage.encodeInPlace();
        }
    }
}
//# sourceMappingURL=DocumentScannerTypes.js.map